"use client";

import Image from "next/image";
import { Images } from "@/assests";
import { motion, useReducedMotion } from "@/lib/motion";
import { Clock } from "@/components/icons";
import PostBreadcrumb from "@/components/ui/post-breadcrumb";
import { HeroGlow } from "@/components/ui";
import { type BlogPostProps } from "./types";
import { categoryMeta, getCategoryColor } from "./data";
import { STATS } from "@/lib/constants";
import {
  StatsCallout,
  ArticleChart,
  CodeBlock,
  AuthorAvatar,
  NewsletterCTA,
  TableOfContents,
  ShareButton,
} from "./parts";

export default function BlogPostSection({ post }: BlogPostProps) {
  const color = getCategoryColor(post.category);
  const icon = categoryMeta[post.category]?.icon;
  const prefersReducedMotion = useReducedMotion();

  return (
    <main id="main-content" tabIndex={-1}>
      <section className="relative overflow-y-clip pt-24">
        <HeroGlow align="left" />
        <div className="relative mx-auto flex max-w-7xl gap-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            className="min-w-0 flex-1"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <PostBreadcrumb
              backHref="/blog"
              backLabel="Blog"
              current={post.category}
              color={color}
              className="mb-6"
            />

            <div className="mb-5 flex flex-wrap items-center gap-3">
              <span
                className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                style={{ color, background: color + "18" }}
              >
                {icon}
                {post.category}
              </span>
              <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <Clock size={11} aria-hidden="true" />
                {post.readTime}
              </span>
              <span className="text-muted-foreground text-xs">{post.date}</span>
            </div>

            <h1 className="text-foreground mb-6 text-3xl leading-tight font-black tracking-tight sm:text-4xl lg:text-5xl">
              {post.title}
            </h1>

            <div className="border-border flex flex-wrap items-center justify-between gap-4 border-b pb-8">
              <div className="flex flex-wrap items-center gap-3">
                <AuthorAvatar initial={post.authorInitial} color={color} />
                <div>
                  <p className="text-foreground text-sm font-semibold">{post.author}</p>
                  <p className="text-muted-foreground text-xs">
                    {post.date} · {post.readTime}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-muted-foreground border-border rounded-full border px-2.5 py-1 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <ShareButton title={post.title} />
            </div>
          </motion.div>
          <div className="hidden w-52 shrink-0 xl:block" aria-hidden="true" />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex items-start gap-16">
          <motion.article
            initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="prose prose-invert max-w-none min-w-0 flex-1"
            style={{
              ["--tw-prose-body" as string]: "var(--muted-foreground)",
              ["--tw-prose-headings" as string]: "var(--foreground)",
              ["--tw-prose-lead" as string]: "var(--muted-foreground)",
            }}
          >
            <div className="border-foreground/6 mb-10 overflow-hidden rounded-2xl border">
              <Image
                src={Images.blogHeroWave}
                alt={post.title}
                width={1200}
                height={480}
                sizes="(min-width: 1024px) 800px, 100vw"
                className="h-auto w-full object-cover"
                loading="eager"
              />
            </div>

            <p className="text-muted-foreground mb-8 text-lg leading-relaxed font-medium">
              {post.excerpt}
            </p>

            <h2
              id="why-js-heavy"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Why JavaScript-heavy sites are harder to scrape
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Traditional scrapers work by fetching raw HTML — but modern single-page applications
              (SPAs) built with React, Vue, or Angular render their content client-side. When you
              request a URL with a basic HTTP client like Python&apos;s{" "}
              <code className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs" style={{ color }}>
                requests
              </code>
              , you get back a near-empty HTML shell with a JavaScript bundle reference — not the
              actual content.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Anti-bot systems exploit this gap. They instrument browser APIs — checking if{" "}
              <code className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs" style={{ color }}>
                navigator.webdriver
              </code>{" "}
              is true, measuring mouse movement entropy, timing keystrokes, and fingerprinting your
              TLS handshake. Without the right setup, you&apos;ll be blocked before you get a single
              byte of useful data.
            </p>

            <StatsCallout />

            <h2
              id="tool-selection"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Choosing the right headless browser
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              You have two primary choices: <strong className="text-foreground">Playwright</strong>{" "}
              and <strong className="text-foreground">Puppeteer</strong>. Both control Chromium
              under the hood, but Playwright&apos;s multi-browser support and superior async API
              make it the better choice for new projects. For stealth, neither is production-ready
              out of the box.
            </p>
            <h3 className="text-foreground mt-8 mb-3 text-lg font-semibold">
              Playwright with stealth patches
            </h3>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              The{" "}
              <code className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs" style={{ color }}>
                playwright-stealth
              </code>{" "}
              package patches the most common detection vectors. Here&apos;s the minimal setup that
              gets you past most Cloudflare and Akamai challenges:
            </p>

            <CodeBlock
              lang="python"
              code={`import asyncio
from playwright.async_api import async_playwright
from playwright_stealth import stealth_async

PROXY = {
    "server": "http://residential.proxidize.com:8080",
    "username": "user123",
    "password": "pass456",
}

async def scrape(url: str) -> str:
    async with async_playwright() as p:
        browser = await p.chromium.launch(
            headless=True,
            proxy=PROXY,
        )
        ctx = await browser.new_context(
            viewport={"width": 1366, "height": 768},
            user_agent=(
                "Mozilla/5.0 (Windows NT 10.0; Win64; x64) "
                "AppleWebKit/537.36 (KHTML, like Gecko) "
                "Chrome/122.0.0.0 Safari/537.36"
            ),
        )
        page = await ctx.new_page()
        await stealth_async(page)
        await page.goto(url, wait_until="networkidle")
        return await page.content()`}
            />

            <h2
              id="proxy-strategy"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Proxy rotation strategies
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Proxy rotation is the single most impactful lever for improving scraping success
              rates. The key insight is matching proxy type to target sophistication:
            </p>

            <ul className="mb-6 list-none space-y-3 pl-0">
              {[
                {
                  type: "Mobile proxies",
                  use: "Most effective against advanced anti-bot systems. Premium pooled mobile IPs with dynamic assignment have the highest trust.",
                },
                {
                  type: "Residential proxies",
                  use: "Ideal for most targets. Wide geographic coverage at scale. Rotate every 5–10 requests for best results.",
                },
                {
                  type: "Datacenter proxies",
                  use: "Fast and cheap, but blocked by most sophisticated targets. Use only for unprotected endpoints.",
                },
              ].map((item) => (
                <li
                  key={item.type}
                  className="border-border/60 bg-card flex gap-3 rounded-xl border p-4"
                >
                  <span
                    className="mt-0.5 h-2 w-2 shrink-0 rounded-full"
                    style={{ background: color, marginTop: 6 }}
                    aria-hidden="true"
                  />
                  <span className="text-muted-foreground text-sm leading-relaxed">
                    <strong className="text-foreground font-semibold">{item.type}:</strong>{" "}
                    {item.use}
                  </span>
                </li>
              ))}
            </ul>

            <ArticleChart />

            <h2
              id="benchmarks"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Benchmarks &amp; success rates
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              We ran 15,000 requests across three target tiers over 30 days using five proxy
              configurations. The results are unambiguous: mobile residential proxies with
              per-request rotation deliver the highest success rate (99.4%) even against Tier-3
              targets using advanced fingerprinting.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              The chart above shows how block rate collapses as request count increases — this is
              the warm-up effect. Once the rotation pool is large enough relative to your request
              volume, the probability of reusing a flagged IP drops below the detection threshold.
            </p>

            <h2
              id="putting-it-together"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Putting it all together
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              A production-grade JavaScript scraper combines three layers of defense: a patched
              headless browser, a rotating residential proxy pool, and intelligent retry logic with
              exponential backoff. Here&apos;s a production-ready wrapper:
            </p>

            <CodeBlock
              lang="python"
              code={`import asyncio
import random
from playwright.async_api import async_playwright
from playwright_stealth import stealth_async

PROXY_POOL = [
    {"server": "http://r.proxidize.com:8080", "username": "u1", "password": "p1"},
    {"server": "http://r.proxidize.com:8081", "username": "u2", "password": "p2"},
]

async def scrape_with_retry(
    url: str,
    max_retries: int = 3,
    backoff: float = 2.0,
) -> str | None:
    for attempt in range(max_retries):
        proxy = random.choice(PROXY_POOL)
        try:
            async with async_playwright() as p:
                browser = await p.chromium.launch(
                    headless=True, proxy=proxy
                )
                page = await (await browser.new_context()).new_page()
                await stealth_async(page)
                await page.goto(url, wait_until="networkidle", timeout=30_000)
                html = await page.content()
                await browser.close()
                return html
        except Exception as exc:
            wait = backoff ** attempt + random.uniform(0, 1)
            print(f"Attempt {attempt + 1} failed ({exc}). Retrying in {wait:.1f}s…")
            await asyncio.sleep(wait)
    return None`}
            />

            <h2
              id="conclusion"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Conclusion
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              JavaScript-heavy scraping is a solvable problem, but it requires treating it as an
              infrastructure challenge rather than a code challenge. The browser is the easy part.
              The proxy pool, rotation strategy, and retry logic are where the real engineering
              lives.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              With Proxidize&apos;s {STATS.ips} residential and mobile IP network, you get the
              widest rotation pool in the industry — giving your scraper the headroom it needs to
              maintain high success rates at any scale.
            </p>

            <div className="border-border/60 mt-10 flex flex-wrap gap-2 border-t pt-8">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="border-border text-muted-foreground hover:border-brand/40 hover:text-foreground cursor-pointer rounded-full border px-3 py-1.5 text-xs transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.article>

          <TableOfContents />
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <NewsletterCTA />
      </div>
    </main>
  );
}
